/**
 * Retry transient upstream failures — the ones where a request never reached a
 * healthy origin, so the identical call a moment later is likely to succeed.
 *
 * WHY THIS EXISTS. `api.onark.app` sits behind Cloudflare, and Cloudflare has
 * intermittently failed to reach the Railway origin behind it: four bursts of
 * error 530 ("Origin DNS error") between 2026-07-29 and 2026-08-25, each
 * lasting seconds and self-healing (Rollbar onark-frontend items 442, 458, 459,
 * 491, 492). Cloudflare answers those with an HTML error page, so
 * `graphql-request` throws a ClientError carrying markup where a GraphQL
 * response should be, and the page render dies on the spot.
 *
 * This does NOT fix the outage. The real fix is the Cloudflare DNS record for
 * the origin (tracked as brain #382) and it lives outside this repo. What this
 * buys is that a sub-second origin wobble stops ending an admin's page load.
 * A multi-second outage will still surface — deliberately, see DEFAULT_DELAYS_MS.
 *
 * Deliberately transport-shaped, not GraphQL-shaped: it duck-types the error
 * rather than importing `graphql-request`, so the worker and API can reuse it
 * for their own upstream calls without dragging a frontend dep into them.
 */

/**
 * Statuses that mean "the origin was not reached", not "the origin said no".
 *
 * 520-527 and 530 are Cloudflare's origin-reachability range — they are emitted
 * by the edge, never by our API. 502/503/504 cover the ordinary gateway cases
 * (Railway rolling a deploy, origin briefly saturated).
 *
 * Notably ABSENT: every 4xx. A 401/403/404/422 is the origin answering, and
 * answering the same way no matter how many times we ask. Retrying those would
 * turn one honest failure into three.
 */
const TRANSIENT_HTTP_STATUS: ReadonlySet<number> = new Set([
  502, 503, 504, 520, 521, 522, 523, 524, 525, 526, 527, 530,
]);

/**
 * Socket/DNS failures where the request died before an HTTP status existed.
 *
 * `EAI_AGAIN` is in (a temporary DNS resolution failure — precisely the
 * client-side twin of the 530s above). `ENOTFOUND` is deliberately OUT: a host
 * that does not resolve at all is usually a misconfigured env var, and that
 * should fail fast and loudly on the first attempt rather than being retried
 * into looking like a flaky network.
 */
const TRANSIENT_NETWORK_CODES: ReadonlySet<string> = new Set([
  'ECONNABORTED',
  'ECONNREFUSED',
  'ECONNRESET',
  'EAI_AGAIN',
  'EPIPE',
  'ETIMEDOUT',
  'UND_ERR_CONNECT_TIMEOUT',
  'UND_ERR_HEADERS_TIMEOUT',
  'UND_ERR_SOCKET',
]);

/**
 * Backoff before each retry, in milliseconds.
 *
 * Total added latency on a doomed request is ~650ms, which is the whole point
 * of the numbers being this small: this runs inside `getServerSideProps`, where
 * every millisecond is a millisecond the admin spends looking at nothing. The
 * observed outages ran ~3s, longer than this budget — so a real outage still
 * reaches the error page rather than being papered over by a slow spinner.
 * Absorbing the blips is worth it; pretending a genuine outage isn't happening
 * is not.
 */
const DEFAULT_DELAYS_MS: readonly number[] = [150, 500];

const DEFAULT_ATTEMPTS = 3;

function sleepFor(ms: number): Promise<void> {
  return new Promise(resolve => {
    setTimeout(resolve, ms);
  });
}

/** Pull an HTTP status off a thrown value, whatever shape it arrived in. */
function statusOf(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null) return undefined;

  // graphql-request's ClientError: { response: { status } }
  const { response } = error as { response?: unknown };

  if (typeof response === 'object' && response !== null) {
    const { status } = response as { status?: unknown };

    if (typeof status === 'number') return status;
  }

  // fetch Response-alikes and hand-rolled errors: { status }
  const { status } = error as { status?: unknown };

  return typeof status === 'number' ? status : undefined;
}

/**
 * Walk the `cause` chain looking for a socket/DNS code.
 *
 * Node wraps these: a failed `fetch` throws `TypeError: fetch failed` whose
 * `cause` carries the real `code`. Reading only the top-level error finds
 * nothing, which is how these get misfiled as generic failures.
 */
function networkCodeOf(error: unknown, depth = 0): string | undefined {
  if (typeof error !== 'object' || error === null || depth > 4) return undefined;

  const { code } = error as { code?: unknown };

  if (typeof code === 'string' && TRANSIENT_NETWORK_CODES.has(code)) {
    return code;
  }

  return networkCodeOf((error as { cause?: unknown }).cause, depth + 1);
}

/**
 * Is this failure worth trying again, unchanged, in a moment?
 *
 * Exported because the decision is the interesting part — it is unit-tested
 * directly, and callers that do their own control flow need the same answer.
 */
export function isTransientUpstreamFailure(error: unknown): boolean {
  const status = statusOf(error);

  if (status !== undefined && TRANSIENT_HTTP_STATUS.has(status)) return true;

  return networkCodeOf(error) !== undefined;
}

export interface RetryTransientOptions {
  /** Total attempts INCLUDING the first. Default 3 (one try, two retries). */
  attempts?: number;
  /** Backoff before retry N. The last entry repeats if attempts outrun it. */
  delaysMs?: readonly number[];
  /** Called before each retry sleeps. Must not throw. */
  onRetry?: (info: {
    attempt: number;
    delayMs: number;
    error: unknown;
  }) => void;
  /** Injectable for hermetic tests — the gate must not spend real seconds. */
  sleep?: (ms: number) => Promise<void>;
}

/**
 * Run `action`, retrying only transient upstream failures.
 *
 * Anything `isTransientUpstreamFailure` rejects is rethrown untouched on the
 * first attempt, so a genuine GraphQL error keeps its original stack and its
 * original timing.
 */
export async function retryTransient<T>(
  action: () => Promise<T>,
  options: RetryTransientOptions = {}
): Promise<T> {
  const {
    attempts = DEFAULT_ATTEMPTS,
    delaysMs = DEFAULT_DELAYS_MS,
    onRetry,
    sleep = sleepFor,
  } = options;

  const totalAttempts = Math.max(1, attempts);

  for (let attempt = 1; ; attempt++) {
    try {
      return await action();
    } catch (error) {
      const isFinalAttempt = attempt >= totalAttempts;

      if (isFinalAttempt || !isTransientUpstreamFailure(error)) throw error;

      const delayMs =
        delaysMs[attempt - 1] ?? delaysMs[delaysMs.length - 1] ?? 0;

      onRetry?.({ attempt, delayMs, error });

      await sleep(delayMs);
    }
  }
}
